import React, { useState } from 'react';
import {
  exportWorkflowsByRequesters,
  loadWorkflowsByRequesters,
} from '../../../actions/reportActions';
import Gravatar from 'react-gravatar';
import { Table, SimpleTextFilter, TableColumn, getVisibleColumns } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import WorkflowLink from '../../common/links/WorkflowLink';
import UserLink from '../../common/links/UserLink';
import { NavLink, useLocation } from 'react-router-dom';
import CompanyLink from '../../common/links/CompanyLink';
import { useTranslation, Trans } from 'react-i18next';

export interface WorkflowsByRequestersListProps {
  currentUser?: any;
}

function renderBasedOnHow(row: any, t: (key: string) => string) {
  switch (row.how?.toLowerCase()) {
    case 'member of group':
      return (
        row.userProfileGroupId && (
          <div>
            <Trans i18nKey="memberOfGroup" values={{ groupName: row.groupName }}>
              Member of{' '}
              <NavLink to={`/settings/user-profile-group/${row.userProfileGroupId}`}>
                {row.groupName}
              </NavLink>
            </Trans>
          </div>
        )
      );
    case 'member of company':
      return (
        row.companyId && (
          <div>
            <Trans i18nKey="memberOfCompany" values={{ companyName: row.companyName }}>
              Member of{' '}
              <NavLink to={`/company/${row.companyId}`}>{row.companyName}</NavLink>
            </Trans>
          </div>
        )
      );
    case 'direct requester':
      return <div>{t('Direct requester')}</div>;
    default:
      return <div>{t('Unknown member')}</div>;
  }
}

function WorkflowsByRequestersList({ currentUser }: WorkflowsByRequestersListProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const allColumns = [
    'keyWorkflowName',
    'fullName',
    'companyName',
    'how',
    'emailAddress',
  ];
  const defaultVisibleColumns = allColumns;
  const [reload] = useState(undefined);
  const [items, setItems] = useState<any>();
  const params = new URLSearchParams(location.search);

  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'workflowsbyrequestersreport-settings',
      getTableSettingsDefaults({
        defaultSort: 'keyWorkflowName',
        defaultVisibleColumns,
      }),
      null,
      loadWorkflowsByRequesters,
      setItems,
      reload,
      t('Failed to load report')
    );

  let columnsConfig: TableColumn[] = [
    {
      Key: 'keyWorkflowName',
      Label: t('Workflow name'),
      Render: (item: any) => (
        <WorkflowLink
          id={item.keyWorkflowId}
          name={item.keyWorkflowName}
          colour={item.keyWorkflowColourHex}
        />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'fullName',
      Label: t('Name'),
      Render: (item: any) => (
        <div style={{ display: 'flex' }}>
          <Gravatar
            email={item?.emailAddress}
            size={20}
            rating="pg"
            default="retro"
            className="img-circle me-3"
          />
          <UserLink
            id={item.userProfileId}
            name={item.fullName}
            imageSize={32}
          />
        </div>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'emailAddress',
      Label: t('Email'),
      Render: (item: any) => item.emailAddress,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'companyName',
      Label: t('Company'),
      Render: (row: any) => (
        <div className="p-2 d-flex align-items-center company-td">
          <CompanyLink
            id={row.companyId}
            name={row.companyName}
            imageId={row.imageId}
            imageSize={32}
            colourHex={row.keyWorkflowColourHex}
          />
        </div>
      ),
    },
    {
      Key: 'how',
      Label: t('How'),
      Render: (row: any) => renderBasedOnHow(row, t),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return items ? (
    <KwPanel className="mb-4">
      <Table
        className="table-v-stripes"
        columns={columns}
        allColumns={allColumns}
        scopes={items.scopes}
        rows={items.data.map((x: any) => ({
          Key: `${x.keyWorkflowId}-${x.userProfileGroupId}-${x.userProfileId}`,
          ...x,
        }))}
        totalRowCount={items.totalRowCount}
        filteredRowCount={items.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportWorkflowsByRequesters}
      />
    </KwPanel>
  ) : (
    <Loading></Loading>
  );
}

export default WorkflowsByRequestersList;
